import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LANGS, LANG_LABELS, subscribeAll } from './model';
import { PrintSheet } from './MenuAdmin';
import './menu.css';

// Public A4 menu (the same sheet as the kitchen's Baskı tab). Linked from the
// confirmation email; guests can save it as PDF with the browser's print dialog.
const T = {
  tr: { pdf: 'PDF indir', print: 'Yazdır', making: 'Hazırlanıyor…', back: 'Menüye dön', loading: 'Menü hazırlanıyor…' },
  en: { pdf: 'Download PDF', print: 'Print', making: 'Preparing…', back: 'Back to menu', loading: 'Preparing the menu…' },
  fr: { pdf: 'Télécharger le PDF', print: 'Imprimer', making: 'Préparation…', back: 'Retour à la carte', loading: 'Préparation de la carte…' },
  es: { pdf: 'Descargar PDF', print: 'Imprimir', making: 'Preparando…', back: 'Volver a la carta', loading: 'Preparando la carta…' },
};

const PrintPage = () => {
  const [params, setParams] = useSearchParams();
  const lang = LANGS.includes(params.get('lang')) ? params.get('lang') : 'tr';
  const t = T[lang];
  const [data, setData] = useState(null);
  const wrap = useRef(null);
  const sheet = useRef(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(null);
  const [making, setMaking] = useState(false);

  // A real PDF file, built in the page: works where window.print() does not
  // (in-app browsers of mail apps on phones).
  const downloadPdf = async () => {
    if (!sheet.current) return;
    setMaking(true);
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
      const el = sheet.current;
      // Capture the sheet unscaled and from the top of the page; the on-screen
      // phone scaling and the scroll offset otherwise shift the image.
      const scaler = el.parentElement;
      const prev = scaler.style.transform;
      scaler.style.transform = 'none';
      const y = window.scrollY;
      window.scrollTo(0, 0);
      let canvas;
      try {
        canvas = await html2canvas(el, {
          scale: 2, backgroundColor: '#1d1819', useCORS: true, scrollX: 0, scrollY: 0,
          windowWidth: Math.max(document.documentElement.clientWidth, el.offsetWidth + 40),
          onclone: (doc) => {
            const sh = doc.querySelector('.lm-sheet');
            if (sh) sh.style.boxShadow = 'none';
          },
        });
      } finally {
        scaler.style.transform = prev;
        window.scrollTo(0, y);
      }
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      // The sheet is dark: fill the whole page so a short menu has no white band.
      pdf.setFillColor(29, 24, 25);
      pdf.rect(0, 0, 210, 297, 'F');
      const w = 210;
      const h = Math.min(297, (canvas.height * w) / canvas.width);
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, w, h);
      pdf.save(`Lucien14-menu-${lang}.pdf`);
    } catch (e) {
      console.error(e);
      window.print();
    } finally {
      setMaking(false);
    }
  };

  useEffect(() => subscribeAll(setData, () => {}), []);
  useEffect(() => { document.title = 'Lucien14 · Menü'; }, []);

  // Scale the A4 sheet down to the screen width (phones); print is unscaled.
  useEffect(() => {
    if (!data) return undefined;
    const measure = () => {
      if (!wrap.current || !sheet.current) return;
      const s = Math.min(1, wrap.current.clientWidth / sheet.current.offsetWidth);
      setScale(s);
      setHeight(sheet.current.offsetHeight * s);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current);
    ro.observe(sheet.current);
    return () => ro.disconnect();
  }, [data]);

  const menuId = params.get('menu') || data?.config?.liveMenuId;

  return (
    <div className="lm" style={{ minHeight: '100vh' }}>
      <div className="lm-admin-top lm-no-print">
        <div className="lm-admin-bar">
          <Link to="/menu" className="lm-btn lm-btn--ghost">← {t.back}</Link>
          <div className="lm-langs" style={{ margin: '0 auto' }}>
            {LANGS.map((l) => (
              <button key={l} type="button" className="lm-lang" aria-pressed={l === lang}
                onClick={() => { params.set('lang', l); setParams(params, { replace: true }); }}>{LANG_LABELS[l]}</button>
            ))}
          </div>
          <div className="lm-row" style={{ gap: 8 }}>
            <button type="button" className="lm-btn" onClick={() => window.print()}>{t.print}</button>
            <button type="button" className="lm-btn lm-btn--primary" disabled={making} onClick={downloadPdf}>{making ? t.making : t.pdf}</button>
          </div>
        </div>
      </div>
      <div className="lm-admin">
        {!data ? <div className="lm-status">{t.loading}</div> : (
          <div className="lm-sheet-wrap" ref={wrap} style={{ height: height ?? undefined }}>
            <div className="lm-sheet-scale" style={{ transform: `scale(${scale})` }}>
              <PrintSheet ref={sheet} data={data} menuId={menuId} lang={lang} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintPage;
